<script setup>
import { usePlatformApi } from '@/utils/platformApi'

// Sama persis pola auth-check pages/platform/tenants/index.vue — lihat
// komentar di sana untuk penjelasan lengkap kenapa public:true di sini
// aman (keamanan sesungguhnya di cookie check + middleware auth:platform
// backend, bukan router guard tenant).
definePage({ meta: { layout: 'platform', public: true } })

const router = useRouter()

const platformAccessToken = useCookie('platformAccessToken')
const platformAdminData = useCookie('platformAdminData')

if (!platformAccessToken.value)
  await router.push('/platform/login')

// ─── State ────────────────────────────────────────────────────────────────
const registrations = ref([])
const loading = ref(false)
const page = ref(1)
const perPage = ref(20)
const totalItems = ref(0)

const headers = [
  { title: 'Bisnis', key: 'business_name', sortable: false },
  { title: 'Pemilik', key: 'owner_name', sortable: false },
  { title: 'Email', key: 'email', sortable: false },
  { title: 'Paket', key: 'plan', sortable: false },

  // Addendum 13, Modul BF: pembeda visual pembayaran Xendit (terverifikasi
  // OTOMATIS via webhook) vs transfer bank manual (perlu dicek admin) —
  // §3.1 poin 1 addendum, supaya kedua tingkat kepercayaan ini tidak
  // disamakan begitu saja.
  { title: 'Metode', key: 'payment_method', sortable: false },
  { title: 'Nominal', key: 'amount', sortable: false, align: 'end' },
  { title: 'Daftar', key: 'created_at', sortable: false },
  { title: 'Aksi', key: 'actions', sortable: false, align: 'center', width: 220 },
]

// Badge pembeda (§3.1 poin 1). pending_verification = belum dicek admin
// sama sekali -- warna warning, paling mencolok. Sudah "Tandai Sudah
// Dibayar" (paid_pending_approval) TETAP dilabeli "Transfer Bank" (bukan
// disamakan visualnya dengan Xendit begitu saja) tapi warna lebih netral
// karena sudah melalui satu lapis cek admin.
const paymentMethodBadge = item => {
  if (item.payment_method !== 'bank_transfer') {
    return { text: 'Xendit', color: 'info', icon: 'ri-bank-card-2-line' }
  }

  if (item.status === 'pending_verification') {
    return { text: 'Transfer Manual — Perlu Cek Mutasi', color: 'warning', icon: 'ri-error-warning-line' }
  }

  return { text: 'Transfer Bank (Sudah Ditandai Dibayar)', color: 'success', icon: 'ri-checkbox-circle-line' }
}

const formatRupiah = val =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(val ?? 0)

// Sama seperti tenants/index.vue: token platform admin sudah tidak valid -> paksa login ulang.
const handleAuthError = async err => {
  const status = err?.status ?? err?.response?.status
  if (status === 401 || status === 403) {
    platformAccessToken.value = null
    platformAdminData.value = null
    await router.push('/platform/login')

    return true
  }

  return false
}

const fetchRegistrations = async () => {
  loading.value = true
  try {
    const res = await usePlatformApi('/api/v1/platform/registrations', {
      query: { page: page.value, per_page: perPage.value },
    }).get()

    const body = res?.data ?? res

    registrations.value = body?.data ?? []
    totalItems.value = body?.pagination?.total ?? 0
  } catch (err) {
    if (await handleAuthError(err)) return

    console.error('[Platform] fetch registrations error', err)
    registrations.value = []
  } finally {
    loading.value = false
  }
}

watch(page, fetchRegistrations, { immediate: true })

// ─── Approve ──────────────────────────────────────────────────────────────
const approveDialog = ref(false)
const approveTarget = ref(null)
const approveLoading = ref(false)
const approveError = ref('')

const openApprove = registration => {
  approveTarget.value = registration
  approveError.value = ''
  approveDialog.value = true
}

const doApprove = async () => {
  approveLoading.value = true
  approveError.value = ''
  try {
    const res = await usePlatformApi(`/api/v1/platform/registrations/${approveTarget.value.id}/approve`).post()
    const body = res?.data ?? res

    approveDialog.value = false
    console.info('[Platform]', body?.message)
    fetchRegistrations()
  } catch (err) {
    if (await handleAuthError(err)) return

    const data = err?.data ?? err?.response?._data

    approveError.value = data?.message ?? 'Gagal menyetujui pendaftaran.'
    console.error('[Platform] approve error', err)
  } finally {
    approveLoading.value = false
  }
}

// ─── Tandai Sudah Dibayar (Addendum 13, Modul BF) ──────────────────────────
// Khusus pendaftar bank_transfer berstatus pending_verification — admin
// SUDAH mencocokkan bukti transfer ke mutasi rekening asli secara manual di
// luar aplikasi ini (⚠️ lihat peringatan §0 addendum: foto bukti bisa
// dipalsukan, jangan approve hanya karena "ada foto"). Aksi ini HANYA
// memindah status ke paid_pending_approval — approve() yang sudah ada
// (Modul I) yang benar-benar membuat tenant, direuse penuh via tombol
// Approve yang sama persis setelah ini.
const markPaidDialog = ref(false)
const markPaidTarget = ref(null)
const markPaidLoading = ref(false)
const markPaidError = ref('')

const openMarkPaid = registration => {
  markPaidTarget.value = registration
  markPaidError.value = ''
  markPaidDialog.value = true
}

const doMarkPaid = async () => {
  markPaidLoading.value = true
  markPaidError.value = ''
  try {
    await usePlatformApi(`/api/v1/platform/registrations/${markPaidTarget.value.id}/mark-as-paid`).post()

    markPaidDialog.value = false
    fetchRegistrations()
  } catch (err) {
    if (await handleAuthError(err)) return

    const data = err?.data ?? err?.response?._data

    markPaidError.value = data?.message ?? 'Gagal menandai pendaftaran sudah dibayar.'
    console.error('[Platform] mark-as-paid error', err)
  } finally {
    markPaidLoading.value = false
  }
}

// ─── Reject ───────────────────────────────────────────────────────────────
const rejectDialog = ref(false)
const rejectTarget = ref(null)
const rejectReason = ref('')
const rejectLoading = ref(false)
const rejectError = ref('')

const openReject = registration => {
  rejectTarget.value = registration
  rejectReason.value = ''
  rejectError.value = ''
  rejectDialog.value = true
}

const doReject = async () => {
  rejectLoading.value = true
  rejectError.value = ''
  try {
    await usePlatformApi(`/api/v1/platform/registrations/${rejectTarget.value.id}/reject`).post({
      body: { rejected_reason: rejectReason.value },
    })

    rejectDialog.value = false
    fetchRegistrations()
  } catch (err) {
    if (await handleAuthError(err)) return

    const data = err?.data ?? err?.response?._data

    // 422 dari RejectRegistrationRequest (rejected_reason kosong) muncul
    // di data.errors.rejected_reason — tampilkan pesan itu kalau ada.
    rejectError.value = data?.errors?.rejected_reason?.[0] ?? data?.message ?? 'Gagal menolak pendaftaran.'
    console.error('[Platform] reject error', err)
  } finally {
    rejectLoading.value = false
  }
}
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center justify-space-between mb-4">
      <div>
        <h4 class="text-h4">
          Persetujuan Pendaftar Baru
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Login sebagai {{ platformAdminData?.name }}
        </p>
      </div>
    </div>

    <VCard>
      <VCardText class="text-body-2 text-medium-emphasis">
        Pendaftar yang sudah membayar (Xendit) atau sudah mengunggah bukti transfer bank manual dan
        menunggu tindakan admin. Pendaftar transfer bank WAJIB dicocokkan ke mutasi rekening asli
        sebelum "Tandai Sudah Dibayar" — foto bukti transfer bisa dipalsukan. Approve akan membuat
        tenant baru secara otomatis beserta akun Owner-nya.
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:page="page"
        :headers="headers"
        :items="registrations"
        :items-length="totalItems"
        :loading="loading"
        :items-per-page="perPage"
        no-data-text="Tidak ada pendaftar yang menunggu tindakan."
      >
        <template #item.plan="{ item }">
          {{ item.plan?.name ?? '-' }} · {{ item.billing_period_label }}
        </template>

        <template #item.payment_method="{ item }">
          <VChip
            :color="paymentMethodBadge(item).color"
            variant="tonal"
            size="small"
            :prepend-icon="paymentMethodBadge(item).icon"
          >
            {{ paymentMethodBadge(item).text }}
          </VChip>
          <VBtn
            v-if="item.transfer_proof_url"
            :href="item.transfer_proof_url"
            target="_blank"
            rel="noopener noreferrer"
            variant="text"
            size="x-small"
            density="compact"
            class="d-block mt-1"
            prepend-icon="ri-image-line"
          >
            Lihat Bukti Transfer
          </VBtn>
        </template>

        <template #item.amount="{ item }">
          <template v-if="item.payment_method === 'bank_transfer'">
            <div class="font-weight-medium">
              {{ formatRupiah(item.total_transfer) }}
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ formatRupiah(item.amount) }} + kode {{ item.unique_code }}
            </div>
          </template>
          <template v-else>
            {{ formatRupiah(item.amount) }}
          </template>
        </template>

        <template #item.created_at="{ item }">
          {{ new Date(item.created_at).toLocaleDateString('id-ID') }}
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex flex-wrap gap-1 justify-center">
            <VBtn
              v-if="item.status === 'pending_verification'"
              color="primary"
              size="small"
              variant="tonal"
              @click="openMarkPaid(item)"
            >
              Tandai Sudah Dibayar
            </VBtn>
            <VBtn
              v-else
              color="success"
              size="small"
              variant="tonal"
              @click="openApprove(item)"
            >
              Approve
            </VBtn>
            <VBtn
              color="error"
              size="small"
              variant="tonal"
              @click="openReject(item)"
            >
              Tolak
            </VBtn>
          </div>
        </template>
      </VDataTableServer>
    </VCard>
  </div>

  <!-- Tandai Sudah Dibayar Dialog (Addendum 13, Modul BF) -->
  <VDialog
    v-model="markPaidDialog"
    max-width="520"
    persistent
  >
    <VCard>
      <VCardTitle class="pt-5 px-5">
        Tandai Sudah Dibayar?
      </VCardTitle>
      <VCardText class="px-5">
        <VAlert
          type="warning"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          Pastikan Anda sudah mengecek mutasi rekening BCA Anda SENDIRI dan nominal
          <strong>{{ formatRupiah(markPaidTarget?.total_transfer) }}</strong> ({{ formatRupiah(markPaidTarget?.amount) }}
          + kode {{ markPaidTarget?.unique_code }}) benar-benar masuk. Foto bukti transfer bisa
          dipalsukan — jangan tandai hanya karena ada foto.
        </VAlert>

        <p class="mb-0">
          Pendaftaran <strong>{{ markPaidTarget?.business_name }}</strong> akan dipindah ke status
          "Menunggu Approval". Belum membuat tenant apa pun — Anda masih perlu klik Approve
          setelahnya.
        </p>

        <VAlert
          v-if="markPaidError"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          {{ markPaidError }}
        </VAlert>
      </VCardText>
      <VCardActions class="px-5 pb-5">
        <VSpacer />
        <VBtn
          variant="tonal"
          :disabled="markPaidLoading"
          @click="markPaidDialog = false"
        >
          Batal
        </VBtn>
        <VBtn
          color="primary"
          :loading="markPaidLoading"
          @click="doMarkPaid"
        >
          Ya, Sudah Saya Cek Mutasinya
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- Approve Dialog -->
  <VDialog
    v-model="approveDialog"
    max-width="480"
    persistent
  >
    <VCard>
      <VCardTitle class="pt-5 px-5">
        Setujui Pendaftaran?
      </VCardTitle>
      <VCardText class="px-5">
        Tenant baru <strong>{{ approveTarget?.business_name }}</strong> akan dibuat otomatis,
        beserta akun Owner untuk <strong>{{ approveTarget?.email }}</strong>. Email berisi info
        login akan dikirim ke pendaftar.

        <VAlert
          v-if="approveTarget?.payment_method === 'bank_transfer'"
          type="info"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          Pendaftar transfer bank manual — sudah ditandai "Sudah Dibayar" berdasarkan cek mutasi
          rekening Anda sebelumnya.
        </VAlert>

        <VAlert
          v-if="approveError"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          {{ approveError }}
        </VAlert>
      </VCardText>
      <VCardActions class="px-5 pb-5">
        <VSpacer />
        <VBtn
          variant="tonal"
          :disabled="approveLoading"
          @click="approveDialog = false"
        >
          Batal
        </VBtn>
        <VBtn
          color="success"
          :loading="approveLoading"
          @click="doApprove"
        >
          Ya, Setujui
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- Reject Dialog -->
  <VDialog
    v-model="rejectDialog"
    max-width="480"
    persistent
  >
    <VCard>
      <VCardTitle class="pt-5 px-5">
        Tolak Pendaftaran?
      </VCardTitle>
      <VCardText class="px-5">
        <p class="mb-3">
          Menolak pendaftaran <strong>{{ rejectTarget?.business_name }}</strong>. Alasan di bawah
          akan dikirim langsung ke email pendaftar — tulis dengan bahasa yang sopan dan jelas.
        </p>
        <VTextarea
          v-model="rejectReason"
          label="Alasan Penolakan"
          placeholder="mis. Data bisnis tidak jelas, Duplikat, dll"
          rows="3"
          :error-messages="rejectError"
        />
      </VCardText>
      <VCardActions class="px-5 pb-5">
        <VSpacer />
        <VBtn
          variant="tonal"
          :disabled="rejectLoading"
          @click="rejectDialog = false"
        >
          Batal
        </VBtn>
        <VBtn
          color="error"
          :loading="rejectLoading"
          @click="doReject"
        >
          Ya, Tolak
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>